import { Injectable } from '@angular/core';
import { logPerformance, logPerformanceWithParams } from '@office-start/telemetry-service';
import { setupTTVRObserver } from '@office-start/ttvr-performance-service';
import { getCLS, getFCP, getFID, getLCP, getTTFB, Metric } from 'web-vitals';

import { InstrumentationParam, InstrumentationService } from '../shared/instrumentation/instrumentation.service';
import { BrowserType, DeviceDetector } from '../shared/services/device-detector.service';
import { SchedulePriority, SchedulerService } from '../shared/services/scheduler.service';

@Injectable({
    providedIn: 'root'
})
export class WebVitalsService {
    private _CLS: Metric; // Cumulative Layout Shift
    private _FCI: number; // First Content Idle
    private _FCP: Metric; // First Contentful Paint
    private _FID: Metric; // First Input Delay
    private _LCP: Metric; // Largest Contentful Paint
    private _TTFB: Metric; // Time to First Byte

    constructor(
        private readonly instrumentationService: InstrumentationService,
        private readonly scheduler: SchedulerService,
        private readonly deviceDetector: DeviceDetector
    ) {
    }

    public getVitalsAndTTVR() {
        const browserType = this.deviceDetector.deviceInfo.browser.browserType;
        const isChromium = browserType === BrowserType.Chrome || browserType === BrowserType.Edge || browserType === BrowserType.Opera;
        let getRelevantVitals: () => any;
        if (isChromium) {
            getRelevantVitals = () => {
                try {
                    const cleanUpTTVRObserver = setupTTVRObserver();
                    getCLS(metric => this.assignMetric(metric));
                    getFCP(metric => this.assignMetric(metric));
                    getFID(metric => this.assignMetric(metric));
                    getTTFB(metric => this.assignMetric(metric));
                    getLCP(metric => {
                        // Cleanup/log main ttvr metric when we log LCP
                        // as the overall TTVR metric should be accurate
                        // at the time of LCP being logged
                        cleanUpTTVRObserver();
                        this.assignMetric(metric);
                    });
                } catch (ex) {
                    /* do nothing */
                }
            };
        } else if (browserType === BrowserType.Firefox || browserType === BrowserType.Safari) {
            getRelevantVitals = () => {
                try {
                    setupTTVRObserver(true);
                    getFCP(metric => this.assignMetric(metric));
                    getFID(metric => this.assignMetric(metric));
                    getTTFB(metric => this.assignMetric(metric));
                } catch (ex) {
                    /* do nothing */
                }
            };
        } else if (browserType === BrowserType.IE) {
            getRelevantVitals = () => {
                try {
                    setupTTVRObserver(true);
                    getTTFB(metric => this.assignMetric(metric));
                } catch (ex) {
                    /* do nothing */
                }
            };
        } else {
            getRelevantVitals = () => 0;
        }
        this.scheduler.scheduleWork(getRelevantVitals, SchedulePriority.VeryLow);
        this._FCI = this.scheduler.FCI;
        this.instrumentationService.logPerformanceEvent('FCI', this._FCI);
        logPerformance({ eventName: 'FCI', latency: this._FCI});
    }

    // Assign the metric to the proper variable
    private assignMetric(metric: Metric ) {
        const { name } = metric;
        switch (name) {
            case 'CLS':
                this._CLS = metric;
                break;
            case 'FCP':
                this._FCP = metric;
                break;
            case 'FID':
                this._FID = metric;
                break;
            case 'LCP':
                this._LCP = metric;
                break;
            case 'TTFB':
                this._TTFB = metric;
                break;
            default:
                break;
        }
        this.logMetric(metric);
    }

    private logMetric(metric: Metric) {
        const { name, value } = metric;
        if (name !== 'CLS') {
            this.instrumentationService.logPerformanceEvent(name, value);
            logPerformance({ eventName: name, latency: value});
        } else {
            const params = { [InstrumentationParam.TotalSize]: String(value) };
            this.instrumentationService.logPerformanceEvent(name, value, params);
            // TODO: Check with Mojgan and Jamila to add TotalSize to performance event schema
            logPerformanceWithParams({ eventName: name, latency: value, instrumentationParams: params });
        }
    }

    public get CLS() {
        return typeof(this._CLS) !== 'undefined' ? this._CLS : -1;
    }

    public get FCI() {
        return typeof(this._FCI) !== 'undefined' ? this._FCI : -1;
    }

    public get FCP() {
        return typeof(this._FCP) !== 'undefined' ? this._FCP : -1;
    }

    public get FID() {
        return typeof(this._FID) !== 'undefined' ? this._FID : -1;
    }

    public get LCP() {
        return typeof(this._LCP) !== 'undefined' ? this._LCP : -1;
    }

    public get TTFB() {
        return typeof(this._TTFB) !== 'undefined' ? this._TTFB : -1;
    }
}
